/**
 * Header State Validation
 *
 * Contains validation utilities for ensuring header state
 * integrity and structure.
 */

/**
 * Validate menu state structure and required fields
 * @param {Object} menuState - Menu state object to validate
 * @returns {boolean} True if menu state is valid
 */
export const validateMenuState = menuState => {
  if (!menuState || typeof menuState !== 'object') {
    return false
  }

  const requiredFields = [
    'isViewFilesMenuVisible',
    'isAccountMenuVisible',
    'isMobileTopLevelMenuVisible',
    'isCreateKinCategoryMenuVisible',
    'isSubNavVisible',
    'currentHeaderClick'
  ]

  return requiredFields.every(field => typeof menuState[field] !== 'undefined')
}

/**
 * Validate search state structure
 * @param {Object} searchState - Search state object to validate
 * @returns {boolean} True if search state is valid
 */
export const validateSearchState = searchState => {
  return (
    searchState &&
    typeof searchState === 'object' &&
    typeof searchState.searchTerm === 'string'
  )
}

/**
 * Validate timeouts state structure
 * @param {Object} timeoutsState - Timeouts state object to validate
 * @returns {boolean} True if timeouts state is valid
 */
export const validateTimeoutsState = timeoutsState => {
  return timeoutsState && typeof timeoutsState === 'object'
}

/**
 * Validate notifications state structure
 * @param {Object} notificationsState - Notifications state object to validate
 * @returns {boolean} True if notifications state is valid
 */
export const validateNotificationsState = notificationsState => {
  return (
    notificationsState &&
    typeof notificationsState === 'object' &&
    typeof notificationsState.showNotification === 'boolean'
  )
}

/**
 * Validate complete header state structure
 * @param {Object} state - Complete header state object to validate
 * @returns {boolean} True if header state is valid
 */
export const validateHeaderState = state => {
  return (
    state &&
    typeof state === 'object' &&
    state.menu &&
    validateMenuState(state.menu) &&
    state.search &&
    validateSearchState(state.search) &&
    state.timeouts &&
    validateTimeoutsState(state.timeouts) &&
    state.notifications &&
    validateNotificationsState(state.notifications)
  )
}

/**
 * Get validation errors for debugging
 * @param {Object} state - Header state object to validate
 * @returns {Array} Array of validation error messages
 */
export const getValidationErrors = state => {
  const errors = []

  if (!state || typeof state !== 'object') {
    errors.push('State must be an object')
    return errors
  }

  if (!state.menu || !validateMenuState(state.menu)) {
    errors.push('Invalid menu state structure')
  }

  if (!state.search || !validateSearchState(state.search)) {
    errors.push('Invalid search state structure')
  }

  if (!state.timeouts || !validateTimeoutsState(state.timeouts)) {
    errors.push('Invalid timeouts state structure')
  }

  if (
    !state.notifications ||
    !validateNotificationsState(state.notifications)
  ) {
    errors.push('Invalid notifications state structure')
  }

  return errors
}
